import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Folder, ExternalLink, Search, Plus, Calendar } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CONTENT } from "@/constants/testIds";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });
const AUTHOR_ROLES = ["autor_area", "administrador_total", "admin"];

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function DocumentsPage() {
  const { hasAnyRole } = useAuth();
  const canCreate = hasAnyRole(AUTHOR_ROLES);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [folderFilter, setFolderFilter] = useState("__all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    ax().get(`${API}/documents`)
      .then(response => {
        setItems(Array.isArray(response.data) ? response.data : []);
        setError("");
      })
      .catch(() => {
        setError("Nao foi possivel carregar os documentos publicados.");
      })
      .finally(() => setLoading(false));
  }, []);

  const folders = useMemo(() => {
    const unique = new Set();
    items.forEach(item => unique.add(item.folder || item.category || "Geral"));
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = items.filter(item => {
    const term = search.toLowerCase();
    const searchable = `${item.title || ""} ${item.content || ""} ${item.folder || ""} ${item.department || ""}`.toLowerCase();
    const matchSearch = searchable.includes(term);
    const folder = item.folder || item.category || "Geral";
    const matchFolder = folderFilter === "__all" || folder === folderFilter;
    return matchSearch && matchFolder;
  });

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-7 xl:px-8">
      <div className="mb-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#eef1ff] px-3 py-1 text-xs font-bold text-[#2346ff]">
            <FileText className="h-4 w-4" />
            Documentos publicados
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-950">Documentos</h1>
          <p className="mt-1 text-sm text-slate-500">
            Arquivos, regulamentos e links publicados pelas areas autorizadas.
          </p>
        </div>

        {canCreate && (
          <Button asChild className="gap-2 bg-[#2346ff] text-white hover:bg-[#1d3ace]">
            <Link to="/gestao-conteudo">
              <Plus className="h-4 w-4" />
              Publicar documento
            </Link>
          </Button>
        )}
      </div>

      <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Buscar documentos..."
            className="pl-9"
          />
        </div>
        <select
          value={folderFilter}
          onChange={event => setFolderFilter(event.target.value)}
          className="min-h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-600 outline-none focus:border-[#2346ff]"
        >
          <option value="__all">Todas as pastas</option>
          {folders.map(folder => <option key={folder} value={folder}>{folder}</option>)}
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2, 3].map(item => <div key={item} className="h-36 animate-pulse rounded-2xl bg-slate-200" />)}
        </div>
      ) : filtered.length ? (
        <div data-testid={CONTENT.contentList} className="grid gap-4 md:grid-cols-2">
          {filtered.map(item => {
            const url = item.url || item.file_url;
            const folder = item.folder || item.category || "Geral";
            return (
              <article key={item.item_id} data-testid={CONTENT.contentItem} className="bento-card group flex flex-col gap-4 p-5">
                <div className="flex items-start gap-3">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#eef1ff] text-[#2346ff]">
                    <FileText className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="line-clamp-2 text-base font-bold text-slate-950 group-hover:text-[#2346ff]">{item.title}</h2>
                    <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-500">{item.content || "Documento publicado para consulta."}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="gap-1 bg-[#eef1ff] text-[#2346ff]">
                    <Folder className="h-3 w-3" />
                    {folder}
                  </Badge>
                  {item.department && <Badge variant="outline">{item.department}</Badge>}
                  {item.updated_at && (
                    <Badge variant="outline" className="gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(item.updated_at)}
                    </Badge>
                  )}
                </div>

                <div className="mt-auto flex justify-end">
                  {url ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-[#2346ff] px-4 text-sm font-bold text-white transition hover:bg-[#1d3ace]"
                    >
                      Abrir documento
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">Sem link anexado</span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="grid min-h-64 place-items-center rounded-3xl border border-dashed border-slate-200 bg-white p-8 text-center">
          <div>
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[#eef1ff] text-[#2346ff]">
              <FileText className="h-7 w-7" />
            </div>
            <h2 className="mt-4 text-base font-bold text-slate-900">Nenhum documento encontrado</h2>
            <p className="mt-1 text-sm text-slate-500">Publique documentos pela Gestao de Conteudo para eles aparecerem aqui.</p>
          </div>
        </div>
      )}
    </div>
  );
}
